import { apiFetch } from '@/lib/api-client';

/**
 * Wire-shape of `GET /api/subscriptions/plans` (Batch 11 Pre-Step,
 * commit 6253c3b). Mirrors `src/subscriptions/dto/plans-response.dto.ts`
 * (cross-repo shape — keep in sync). Public endpoint, no auth header
 * required.
 *
 * Consumed by `useSubscriptionPlans()` (Commit 2) which caches the
 * result module-level so A-02 Browse-Mode and Pakete renders share a
 * single fetch across the session.
 */
export interface PublicPlan {
  /** Catalog identifier (e.g. `entdecker-monatlich`). */
  planKey: string;
  /**
   * SubscriptionTierSlug for subscription entries (`entdecker` /
   * `abenteurer` / `geschichten_profi`). Empty string for package
   * entries (no tier). PWA discriminates sub-vs-pack via `mode` /
   * `billingInterval`, NOT via tierKey-truthiness.
   */
  tierKey: string;
  /** User-facing German name (verbatim from briefing). */
  planName: string;
  /** `monthly` / `yearly` for subscriptions, `one-time` for packages. */
  billingInterval: 'monthly' | 'yearly' | 'one-time';
  /** Stripe Checkout Session mode. */
  mode: 'subscription' | 'payment';
  /** Total amount charged per billing period, in EUR. */
  amountEuros: number;
  /** Monthly story allowance for subscriptions; null for packages. */
  storiesPerMonth: number | null;
  /** One-time stories granted for packages; null for subscriptions. */
  storiesGranted: number | null;
  /**
   * Stripe Price ID — read-only for client-side analytics passthrough.
   * Server-side checkout-session creation re-resolves via `planKey`
   * (the price ID is never trusted from the client).
   */
  stripePriceId: string;
}

/**
 * Fetch the public plan catalog. Single request returns all 8 entries
 * (3 tiers × 2 cycles + 2 packs) in catalog source-file order:
 * subscriptions tier-then-cycle, then packs.
 */
export function fetchPlans(): Promise<PublicPlan[]> {
  return apiFetch<PublicPlan[]>('/api/subscriptions/plans');
}
